import { create } from 'zustand'
import { devtools } from 'zustand/middleware'
import axios from 'axios'
import { UserData, UserModel } from '../app/modules/auth/models/UserModel'
import { Organization } from '../app/modules/accounts/components/organization/types'
import { NotificationType } from '../app/modules/types/notification'
import { PickedScanNode } from '../app/modules/types/scan'

/**
 * @deprecated Use React Query hooks instead. This interface is kept for backwards compatibility.
 */
export interface BasicFilter {
  options: any
  selected: any
  sortAsc: string
  sortDes: string
  search: { name: string; value: string }[]
}

interface NodeConnect {
  nodeId: string
  portId?: string
}

/**
 * Legacy data state interface - kept for backwards compatibility
 * @deprecated These should be migrated to React Query
 */
interface LegacyDataState {
  selectedVulnId: string
  dataEmails: any
  dataReportHistory: any
  dataIp: any
  dataPoC: any
  dataVulDetails: any
  dataReportScan: any
  nodeDetailsScanDag: PickedScanNode | undefined
  dataNotification: any
  dataTicket: any
  dataWorkflow: any
}

/**
 * Authentication state interface
 * Data caching has been moved to React Query, but legacy `data` property
 * is kept for backwards compatibility during migration
 * @interface IAuthState
 */
export interface IAuthState {
  /** Current user information */
  user: UserModel | undefined | null
  userProfile: UserData | undefined | null

  /** Flag for new notifications */
  newNotification: boolean
  /** Nodes can a Node be connected to */
  nodeConnect: null | NodeConnect[]
  /** Public workflow flag */
  isPublicWorkflow: boolean
  /** Current organization ID */
  orgId: string
  /** Current organization name */
  orgName: string
  /** User organization flag */
  userOrg: boolean | null | undefined
  activeOrganization: string | undefined
  /** List of organizations */
  dataOrgList: Organization[] | null | undefined
  /** User permission list */
  permissionList: string[]
  /** AI assistant related state */
  aiAssistant: Record<
    string,
    {
      vulID: string | null
      id: null | string
      title: null | string
    }
  >
  /** Notification data */
  dataNotification: NotificationType[] | undefined | null
  /** Selected vulnerability ID for AI assistant */
  selectedVulnId: string
  /**
   * Which entry point opened the AI assistant.
   *
   * Governs ONLY the case where no chat is bound yet: `'finding'` asks the user
   * to pick a vulnerability first, `'free'` offers a chat with no subject at
   * all. Once a chat IS bound the two are identical, so nothing downstream
   * branches on it.
   */
  aiEntryPoint: 'finding' | 'free'
  /** @deprecated Legacy data object - use React Query hooks instead */
  data: LegacyDataState
}

/**
 * Auth actions interface
 */
interface AuthActions {
  // Organization Management
  SetOrgList: (payload: Organization[] | null | undefined) => void
  SetOrgId: (payload: string) => void
  SetOrgName: (payload: string) => void
  SetActiveOrganization: (payload: string) => void
  SetUserOrg: (payload: boolean | null | undefined) => void
  SetDataPermissionList: (payload: string[]) => void

  // Authentication
  Logout_: (payload: boolean) => void
  UserLoaded: (payload: UserModel | undefined | null) => void
  UserProfile: (payload: UserData | undefined | null) => void
  ChangeUserHasNewNotification: (payload: boolean) => void
  ChangeNotificationStatus: (payload: boolean) => void

  // Workflow
  TogglePublicWorkflow: (payload: boolean) => void
  SetNodeConect: (payload: NodeConnect[] | null) => void

  // AI Assistant
  SetAiAssistantVulnerabilitiesID: (payload: {
    vulID: string | null
    id: string | null
    title: string | null
  }) => void
  SetSelectedVulnId: (payload: string) => void
  SetAiEntryPoint: (payload: 'finding' | 'free') => void

  // Legacy Data (deprecated)
  FilterSelectedTicket: (payload: any) => void
  SetDataPoc: (payload: any) => void
  SetDataReportHistory: (payload: any) => void
  SetDataReportScan: (payload: any) => void
  SetDataTicket: (payload: any) => void
  SetDataVulDetails: (payload: any) => void
  setNodeDetailsScanDag: (payload: PickedScanNode | undefined) => void
  SetWorkflowData: (payload: any) => void
}

/**
 * Combined store type
 */
type AuthStore = IAuthState & AuthActions

/**
 * Initial state for the authentication store
 */
const initialAuthState: IAuthState = {
  user: null,
  userProfile: null,
  newNotification: false,
  nodeConnect: null,
  orgId: '',
  orgName: '',
  userOrg: false,
  activeOrganization: undefined,
  dataOrgList: null,
  isPublicWorkflow: false,
  permissionList: [],
  aiAssistant: {} as Record<
    string,
    {
      vulID: string | null
      id: string | null
      title: string | null
    }
  >,
  dataNotification: undefined,
  selectedVulnId: '',
  aiEntryPoint: 'finding',
  data: {
    selectedVulnId: '',
    dataEmails: undefined,
    dataReportHistory: undefined,
    dataIp: undefined,
    dataPoC: undefined,
    dataVulDetails: undefined,
    dataReportScan: undefined,
    nodeDetailsScanDag: undefined,
    dataNotification: undefined,
    dataTicket: undefined,
    dataWorkflow: undefined,
  },
}

/**
 * Zustand store for authentication and global state
 */
export const useAuthStore = create<AuthStore>()(
  devtools(
    (set) => ({
      // State
      ...initialAuthState,

      // ==================== Organization Management ====================
      SetOrgList: (payload) =>
        set(
          (state) => ({
            dataOrgList: payload,
          }),
          false,
          'SetOrgList'
        ),
      SetActiveOrganization: (payload) =>
        set(
          (state) => ({
            activeOrganization: payload,
          }),
          false,
          'SetActiveOrganization'
        ),

      SetOrgId: (payload) =>
        set(
          (state) => ({
            orgId: payload,
            aiAssistant: initialAuthState.aiAssistant,
            aiEntryPoint: initialAuthState.aiEntryPoint,
          }),
          false,
          'SetOrgId'
        ),

      SetOrgName: (payload) =>
        set(
          (state) => ({
            orgName: payload,
          }),
          false,
          'SetOrgName'
        ),

      SetUserOrg: (payload) =>
        set(
          (state) => ({
            userOrg: payload,
          }),
          false,
          'SetUserOrg'
        ),

      SetDataPermissionList: (payload) =>
        set(
          (state) => ({
            permissionList: payload,
          }),
          false,
          'SetDataPermissionList'
        ),

      // ==================== Authentication ====================
      Logout_: (payload) => {
        const fcmToken = localStorage.getItem('fcmToken')
        if (payload) {
          axios
            .post(
              `${window?.RUNTIME_CONFIG?.REACT_APP_API_ENDPOINT || process.env.REACT_APP_API_ENDPOINT}/auth/jwt/logout/`,
              { fcm_token: fcmToken }
            )
            .then(() => { })
            .catch(() => { })
          localStorage.removeItem('fcmToken')
          localStorage.removeItem('fcmTokenExpiry')
        } else {
          localStorage.removeItem('fcmToken')
          localStorage.removeItem('fcmTokenExpiry')
        }
        set(initialAuthState, false, 'Logout_')
      },

      UserLoaded: (payload) =>
        set(
          (state) => ({
            user: payload,
          }),
          false,
          'UserLoaded'
        ),
      UserProfile: (payload) =>
        set(
          (state) => ({
            userProfile: payload,
          }),
          false,
          'UserProfile'
        ),

      ChangeUserHasNewNotification: (payload) =>
        set(
          (state) => ({
            user: state.user
              ? { ...state.user, has_new_notification: payload }
              : state.user,
          }),
          false,
          'ChangeUserHasNewNotification'
        ),

      ChangeNotificationStatus: (payload) =>
        set(
          (state) => ({
            newNotification: payload,
          }),
          false,
          'ChangeNotificationStatus'
        ),

      // ==================== Workflow ====================
      TogglePublicWorkflow: (payload) =>
        set(
          (state) => ({
            isPublicWorkflow: payload,
          }),
          false,
          'TogglePublicWorkflow'
        ),

      SetNodeConect: (payload) =>
        set(
          (state) => ({
            nodeConnect: payload || null,
          }),
          false,
          'SetNodeConect'
        ),

      // ==================== AI Assistant ====================
      SetAiAssistantVulnerabilitiesID: (payload) =>
        set(
          (state) => {
            if (payload.vulID) {
              return {
                aiAssistant: {
                  ...state.aiAssistant,
                  [payload.vulID]: {
                    vulID: payload.vulID,
                    id: payload.id ?? null,
                    title: payload.title ?? null,
                  },
                },
              }
            }
            return state
          },
          false,
          'SetAiAssistantVulnerabilitiesID'
        ),

      SetSelectedVulnId: (payload) =>
        set(
          (state) => ({
            selectedVulnId: payload,
          }),
          false,
          'SetSelectedVulnId'
        ),

      SetAiEntryPoint: (payload) =>
        set(() => ({ aiEntryPoint: payload }), false, 'SetAiEntryPoint'),

      // ==================== Legacy Data ====================
      FilterSelectedTicket: (payload) =>
        set(
          (state) => ({
            data: {
              ...state.data,
              dataTicket: state.data.dataTicket
                ? { ...state.data.dataTicket, selected: payload }
                : state.data.dataTicket,
            },
          }),
          false,
          'FilterSelectedTicket'
        ),

      SetDataPoc: (payload) =>
        set(
          (state) => ({
            data: { ...state.data, dataPoC: payload },
          }),
          false,
          'SetDataPoc'
        ),

      SetDataReportHistory: (payload) =>
        set(
          (state) => ({
            data: { ...state.data, dataReportHistory: payload },
          }),
          false,
          'SetDataReportHistory'
        ),

      SetDataReportScan: (payload) =>
        set(
          (state) => ({
            data: { ...state.data, dataReportScan: payload },
          }),
          false,
          'SetDataReportScan'
        ),

      SetDataTicket: (payload) =>
        set(
          (state) => ({
            data: { ...state.data, dataTicket: payload },
          }),
          false,
          'SetDataTicket'
        ),

      SetDataVulDetails: (payload) =>
        set(
          (state) => ({
            data: { ...state.data, dataVulDetails: payload },
          }),
          false,
          'SetDataVulDetails'
        ),

      setNodeDetailsScanDag: (payload) =>
        set(
          (state) => ({
            data: { ...state.data, nodeDetailsScanDag: payload },
          }),
          false,
          'setNodeDetailsScanDag'
        ),

      SetWorkflowData: (payload) =>
        set(
          (state) => ({
            data: { ...state.data, dataWorkflow: payload },
          }),
          false,
          'SetWorkflowData'
        ),
    }),
    { name: 'AuthStore' }
  )
)
